import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Checkbox } from '@malash-office/ui-components';
import { formatFileSize } from '@/components/common/media/mediaValidation';
import { FEEDBACK_INNER_RADIUS, feedbackSurfaces } from '../feedbackTones';
import type { NewFeedbackForm } from './useNewFeedbackForm';

/** The extension badge — `PNG`, `PDF`. */
export const FileBadge = ({ name }: { name: string }) => (
  <Box
    component="span"
    sx={{
      fontSize: 10,
      fontWeight: 800,
      color: 'primary.main',
      bgcolor: 'primary.light',
      borderRadius: '5px',
      px: 0.75,
      py: '3px',
      flex: 'none'
    }}
  >
    {(name.split('.').pop() ?? '').toUpperCase().slice(0, 5)}
  </Box>
);

/**
 * "קבצים ותמונות" — the screenshot tile beside the drop area, then the files added.
 *
 * The screenshot tile shows the picture actually taken (not the mockup's sketch of one) and dims
 * when it will not be attached. When the capture failed the checkbox is off and cannot be ticked.
 */
const FeedbackFilesField = ({ form, screenshotUrl }: { form: NewFeedbackForm; screenshotUrl: string | null }) => {
  const theme = useTheme();
  const surfaces = feedbackSurfaces(theme);
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700 }}>
        קבצים ותמונות
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 1.25 }}>
        <Box
          sx={{
            borderRadius: FEEDBACK_INNER_RADIUS,
            bgcolor: surfaces.well,
            p: 1.25,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            opacity: form.withScreenshot ? 1 : 0.55
          }}
        >
          <Box
            sx={{
              height: 84,
              borderRadius: '9px',
              bgcolor: 'common.white',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {screenshotUrl ? (
              <Box
                component="img"
                src={screenshotUrl}
                alt="צילום מסך של העמוד"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
              />
            ) : (
              <Box sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>לא ניתן היה לצלם את המסך</Box>
            )}
          </Box>
          <Checkbox
            checked={form.withScreenshot}
            disabled={!screenshotUrl}
            onChange={form.toggleScreenshot}
            label="צרף צילום מסך של העמוד"
          />
        </Box>

        <Box
          role="button"
          tabIndex={0}
          onClick={() => input.current?.click()}
          onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && input.current?.click()}
          onDragOver={event => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setDragging(false);
            form.addFiles(Array.from(event.dataTransfer.files));
          }}
          sx={{
            borderRadius: FEEDBACK_INNER_RADIUS,
            border: `1.5px dashed ${theme.palette.grey[100]}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            p: 1.5,
            minHeight: 120,
            cursor: 'pointer',
            textAlign: 'center',
            bgcolor: dragging ? surfaces.rowHover : 'transparent',
            '&:hover': { bgcolor: surfaces.rowHover }
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 16v3h14v-3"
              stroke={theme.palette.primary.main}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>
            גרור קובץ או לחץ להעלאה
          </Box>
          <Box component="span" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
            תמונה, PDF, וידאו קצר · עד 20MB
          </Box>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            accept="image/*,application/pdf,video/mp4,video/webm,video/quicktime"
            onChange={event => {
              form.addFiles(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
        </Box>
      </Box>

      {form.files.map((file, index) => (
        <Box
          key={`${file.name}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1,
            borderRadius: FEEDBACK_INNER_RADIUS,
            bgcolor: surfaces.rowHover,
            border: `1px solid ${surfaces.rowLine}`
          }}
        >
          <FileBadge name={file.name} />
          <Box
            component="span"
            dir="ltr"
            sx={{
              flex: 1,
              fontSize: 12.5,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'right'
            }}
          >
            {file.name}
          </Box>
          <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
            {formatFileSize(file.size)}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => form.removeFile(index)}
            sx={{
              border: 'none',
              background: 'none',
              p: 0,
              fontFamily: 'inherit',
              fontSize: 11.5,
              fontWeight: 700,
              color: 'error.main',
              cursor: 'pointer'
            }}
          >
            הסר
          </Box>
        </Box>
      ))}
      {form.fileErrors.map(error => (
        <Box key={error} sx={{ fontSize: 11.5, fontWeight: 600, color: 'error.main' }}>
          {error}
        </Box>
      ))}
    </Box>
  );
};

export default FeedbackFilesField;
